import { registerFeatureBootstrap } from "../bootstrap";
import { closestFromEvent } from "../utils/dom";
import {
  defaultHubRuntimeEnv,
  postHubForm,
  readHubErrorMessage,
  setHubFeedback,
  unwrapHubData,
  type HubRuntimeEnv,
} from "./hub_actions";

const SOCIAL_CAPITAL_HUB_SELECTOR = "[data-social-capital-hub-root]";
const HOLD_DURATION_MS = 1500;

const cleanupByRoot = new WeakMap<HTMLElement, () => void>();

function nodeWithinRoot(root: HTMLElement, node: HTMLElement | null): HTMLElement | null {
  return node instanceof HTMLElement && root.contains(node) ? node : null;
}

function feedbackNode(root: HTMLElement): HTMLElement | null {
  const node = root.querySelector("[data-social-capital-feedback]");
  return node instanceof HTMLElement ? node : null;
}

function showSocialFeedback(root: HTMLElement, message: string, isError: boolean): void {
  setHubFeedback(feedbackNode(root), message, isError);
}

function socialForm(root: HTMLElement): HTMLFormElement | null {
  const node = root.querySelector("[data-social-capital-give]");
  return node instanceof HTMLFormElement ? node : null;
}

function socialRitual(root: HTMLElement): HTMLElement | null {
  const node = root.querySelector("[data-social-ritual]");
  return node instanceof HTMLElement ? node : null;
}

function socialRitualName(root: HTMLElement): HTMLElement | null {
  const node = root.querySelector("[data-social-ritual-name]");
  return node instanceof HTMLElement ? node : null;
}

function recipientSelect(root: HTMLElement): HTMLSelectElement | null {
  const node = root.querySelector("#social_recipient");
  return node instanceof HTMLSelectElement ? node : null;
}

function openSocialRitual(root: HTMLElement, recipientID: string, recipientName: string): void {
  const select = recipientSelect(root);
  const ritual = socialRitual(root);
  const title = socialRitualName(root);

  if (select && recipientID) {
    select.value = recipientID;
  }
  if (title) {
    title.textContent = recipientName || select?.selectedOptions?.[0]?.textContent || "Chosen recipient";
  }
  if (ritual) {
    ritual.hidden = false;
  }
}

function closeSocialRitual(root: HTMLElement): void {
  const ritual = socialRitual(root);
  if (ritual) {
    ritual.hidden = true;
  }
}

async function submitSocialOffer(root: HTMLElement, env: HubRuntimeEnv): Promise<void> {
  const form = socialForm(root);
  const url = String(form?.getAttribute("data-social-capital-give") || "").trim();
  if (!(form instanceof HTMLFormElement) || !url) {
    return;
  }
  const result = await postHubForm(url, new FormData(form));
  if (!result.ok) {
    showSocialFeedback(root, readHubErrorMessage(result.error, "Transfer failed."), true);
    return;
  }

  const data = unwrapHubData<{ remaining_tokens?: number }>(result.value);
  showSocialFeedback(root, `Token sent. Remaining: ${data?.remaining_tokens ?? "?"}`, false);
  env.WMUHaptic?.play?.("success");
  env.location.reload();
}

function bindSocialClicks(root: HTMLElement, signal: AbortSignal): void {
  root.addEventListener("click", (event) => {
    const offerButton = nodeWithinRoot(root, closestFromEvent(event, "[data-social-offer-start]"));
    if (offerButton instanceof HTMLButtonElement) {
      openSocialRitual(
        root,
        String(offerButton.getAttribute("data-social-offer-start") || "").trim(),
        String(offerButton.getAttribute("data-social-offer-name") || "").trim(),
      );
      return;
    }

    const showAllButton = nodeWithinRoot(root, closestFromEvent(event, "[data-social-show-all]"));
    if (showAllButton instanceof HTMLButtonElement) {
      const roster = root.querySelector("[data-social-roster]");
      if (roster instanceof HTMLElement) {
        roster.hidden = !roster.hidden;
        showAllButton.textContent = roster.hidden ? "Show All" : "Hide All";
      }
      return;
    }

    const openButton = nodeWithinRoot(root, closestFromEvent(event, "[data-social-open-ritual]"));
    if (openButton instanceof HTMLButtonElement) {
      const select = recipientSelect(root);
      const option = select?.selectedOptions?.[0] || null;
      if (!(select instanceof HTMLSelectElement) || !select.value) {
        showSocialFeedback(root, "Choose a participant first.", true);
        return;
      }
      openSocialRitual(root, select.value, option?.textContent || "");
      return;
    }

    const cancelButton = nodeWithinRoot(root, closestFromEvent(event, "[data-social-ritual-cancel]"));
    if (cancelButton instanceof HTMLButtonElement) {
      closeSocialRitual(root);
    }
  }, { signal });
}

function bindSocialSubmit(root: HTMLElement, env: HubRuntimeEnv, signal: AbortSignal): void {
  root.addEventListener("submit", (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !root.contains(form) || !form.matches("[data-social-capital-give]")) {
      return;
    }
    event.preventDefault();
    void submitSocialOffer(root, env);
  }, { signal });
}

function bindSocialHold(root: HTMLElement, env: HubRuntimeEnv): () => void {
  const holdButton = root.querySelector("[data-social-hold-button]");
  const form = socialForm(root);
  if (!(holdButton instanceof HTMLButtonElement) || !(form instanceof HTMLFormElement)) {
    return () => undefined;
  }

  let holdTimer = 0;

  const clearHold = (): void => {
    holdButton.classList.remove("is-holding");
    env.clearTimeout(holdTimer);
    holdTimer = 0;
  };

  const startHold = (event?: PointerEvent | MouseEvent): void => {
    if (event instanceof MouseEvent && typeof event.button === "number" && event.button !== 0) {
      return;
    }
    clearHold();
    holdButton.classList.add("is-holding");
    holdTimer = env.setTimeout(() => {
      closeSocialRitual(root);
      form.requestSubmit();
    }, HOLD_DURATION_MS);
  };

  holdButton.addEventListener("pointerdown", startHold);
  ["pointerup", "pointerleave", "pointercancel"].forEach((eventName) => {
    holdButton.addEventListener(eventName, clearHold);
  });

  return () => {
    clearHold();
    holdButton.removeEventListener("pointerdown", startHold);
    ["pointerup", "pointerleave", "pointercancel"].forEach((eventName) => {
      holdButton.removeEventListener(eventName, clearHold);
    });
  };
}

export function mountSocialCapitalHub(root: HTMLElement, env: HubRuntimeEnv = defaultHubRuntimeEnv()): void {
  const ctrl = new AbortController();
  bindSocialClicks(root, ctrl.signal);
  bindSocialSubmit(root, env, ctrl.signal);
  const releaseHold = bindSocialHold(root, env);
  cleanupByRoot.set(root, () => {
    ctrl.abort();
    releaseHold();
  });
}

export function unmountSocialCapitalHub(root: HTMLElement): void {
  cleanupByRoot.get(root)?.();
  cleanupByRoot.delete(root);
}

registerFeatureBootstrap({
  key: "participant:social-capital-hub",
  selector: SOCIAL_CAPITAL_HUB_SELECTOR,
  mount(root) {
    mountSocialCapitalHub(root);
  },
  unmount(root) {
    unmountSocialCapitalHub(root);
  },
});
